// Registering a batch and its production chain (coordinator review point 5; needed before real data can be entered).
// Each step of the chain is either a new record, an existing one, or left as it is. Records are append-only: a node
// cannot be renamed or deleted, and a link is corrected by a newer link (latest wins), so everything is checked and
// shown for confirmation before anything is written. The database decides who may create what (row-level security).
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { PageHeader } from '../components/Layout.tsx';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { activePack, allNodes, links, whoAmI } from '../lib/data.ts';
import { operatorClient } from '../lib/supabase.ts';
import type { Org } from '../lib/template.ts';
import type { NodeRow } from '../lib/types.ts';

// upstream first, the way a batch is produced
const KINDS = ['raw_water', 'strain_lot', 'cultivation', 'harvest', 'parent', 'product_batch'] as const;
type Kind = (typeof KINDS)[number];
// a step can stand for the one above it (DECISIONS E-15): no separate harvest or processing record
const SAME_AS: Partial<Record<Kind, Kind>> = { harvest: 'cultivation', parent: 'harvest' };
const WRITE_ROLES = ['data_provider', 'quality_manager', 'regulatory_owner', 'platform_admin'];
const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]{1,63}$/;

type Mode = 'none' | 'existing' | 'new' | 'same';
interface StepState {
  mode: Mode;
  id: string;
  owner: string;
}
interface Product {
  prod_code: string;
  product_name: string;
  cultivation_medium_code?: string;
}
/** The node a step resolves to: its own id, or the node of the step it is the same as. */
function resolve(steps: Record<Kind, StepState>, k: Kind): { id: string; kind: Kind } | null {
  const s = steps[k];
  if (s.mode === 'same') {
    const up = SAME_AS[k];
    return up ? resolve(steps, up) : null;
  }
  if (s.mode === 'none' || !s.id.trim()) return null;
  return { id: s.id.trim(), kind: k };
}

interface LinkRow {
  from_node: string;
  link: string;
  to_node: string | null;
  same_as: string | null;
}

export function RegisterBatch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<NodeRow[] | null>(null);
  const [current, setCurrent] = useState<LinkRow[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [me, setMe] = useState<{ org: string | null; roles: string[] } | null>(null);
  const [steps, setSteps] = useState<Record<Kind, StepState>>(
    () =>
      Object.fromEntries(KINDS.map((k) => [k, { mode: 'none', id: '', owner: '' }])) as Record<
        Kind,
        StepState
      >,
  );
  const [prodCode, setProdCode] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [n, l, o, pack, who] = await Promise.all([
          allNodes(),
          links(),
          operatorClient().from('organisations').select('org_code, name, aliases').order('name'),
          activePack(),
          whoAmI(),
        ]);
        setNodes(n);
        setCurrent(l);
        setOrgs((o.data ?? []) as Org[]);
        setProducts(
          (pack?.pack as unknown as { products?: { items: Product[] } } | undefined)?.products?.items ?? [],
        );
        setMe({ org: who?.org ?? null, roles: who?.roles ?? [] });
        setSteps((s) => {
          const next = { ...s };
          for (const k of KINDS) next[k] = { ...next[k], owner: who?.org ?? '' };
          next.product_batch = { ...next.product_batch, mode: 'new' };
          return next;
        });
      } catch (e) {
        setError(e);
      }
    })();
  }, []);

  const product = products.find((p) => p.prod_code === prodCode);
  const wastewater = product?.cultivation_medium_code === 'WW';
  // a data provider creates records for their own organisation only (the database enforces it as well)
  const ownOnly = !!me && me.roles.includes('data_provider') && !me.roles.some((r) => r !== 'data_provider');
  const canWrite = !!me && me.roles.some((r) => WRITE_ROLES.includes(r));

  const set = (k: Kind, patch: Partial<StepState>) => {
    setConfirming(false);
    setSteps((s) => ({ ...s, [k]: { ...s[k], ...patch } }));
  };

  const plan = useMemo(() => {
    const problems: string[] = [];
    const known = new Set((nodes ?? []).map((n) => n.node_id));
    const newIds = new Set<string>();
    const create: { node_id: string; kind: Kind; prod_code: string | null; owner_org: string }[] = [];
    for (const k of KINDS) {
      const s = steps[k];
      const label = t(`kind.${k}`);
      if (s.mode === 'same' && steps[SAME_AS[k] as Kind].mode === 'same')
        problems.push(t('register.problem.double_same'));
      if (s.mode === 'same' && steps[SAME_AS[k] as Kind].mode === 'none')
        problems.push(t('register.problem.same_needs_step', { step: label }));
      if (s.mode === 'existing' && !s.id) problems.push(t('register.problem.choose', { step: label }));
      if (s.mode !== 'new') continue;
      const id = s.id.trim();
      if (!id) problems.push(t('register.problem.no_id', { step: label }));
      else if (!ID_PATTERN.test(id)) problems.push(t('register.problem.bad_id', { id }));
      else if (known.has(id)) problems.push(t('register.problem.taken', { id }));
      else if (newIds.has(id)) problems.push(t('register.problem.twice', { id }));
      newIds.add(id);
      if (!s.owner) problems.push(t('register.problem.no_owner', { step: label }));
      if (k === 'product_batch' && !prodCode) problems.push(t('register.problem.no_product'));
      create.push({
        node_id: id,
        kind: k,
        prod_code: k === 'product_batch' ? prodCode || null : null,
        owner_org: s.owner,
      });
    }
    if (steps.raw_water.mode !== 'none' && product && !wastewater)
      problems.push(t('register.problem.water_not_ww'));

    // links run from each record to the one it came from; an unchanged link is not written again
    const edges: [Kind, Kind][] = [
      ['product_batch', 'parent'],
      ['parent', 'harvest'],
      ['harvest', 'cultivation'],
      ['cultivation', 'strain_lot'],
      ['cultivation', 'raw_water'],
    ];
    const add: LinkRow[] = [];
    for (const [from, to] of edges) {
      if (steps[from].mode === 'same' || steps[from].mode === 'none') continue;
      const src = resolve(steps, from);
      const dst = resolve(steps, to);
      if (!src || !dst) continue;
      const row = {
        from_node: src.id,
        link: to,
        to_node: dst.id,
        same_as: dst.kind === to ? null : dst.kind,
      };
      const now = current.find((l) => l.from_node === row.from_node && l.link === row.link);
      if (now && now.to_node === row.to_node && (now.same_as ?? null) === row.same_as) continue;
      add.push(row);
    }
    if (create.length === 0 && add.length === 0) problems.push(t('register.problem.nothing'));
    return { problems: [...new Set(problems)], create, add };
  }, [steps, nodes, current, prodCode, product, wastewater, t]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const sb = operatorClient();
      if (plan.create.length > 0) {
        const { error: e } = await sb.from('batch_nodes').insert(plan.create);
        if (e) throw new Error(e.message);
      }
      if (plan.add.length > 0) {
        const { error: e } = await sb.from('node_links').insert(plan.add);
        if (e) throw new Error(e.message);
      }
      const batch = resolve(steps, 'product_batch');
      void navigate(batch ? `/batches/${encodeURIComponent(batch.id)}/genealogy` : '/batches');
    } catch (e) {
      setError(e);
      setConfirming(false);
      // whatever was written stays; reload so a retry treats it as existing
      setNodes(await allNodes());
      setCurrent(await links());
    } finally {
      setBusy(false);
    }
  }

  if (!nodes || !me) return <Loading />;
  if (!canWrite)
    return (
      <main>
        <PageHeader title={t('register.title')} page="register" />
        <p className="hint">{t('register.read_only')}</p>
      </main>
    );

  const orgName = (code: string) => orgs.find((o) => o.org_code === code)?.name ?? code;
  const shown = KINDS.filter((k) => k !== 'raw_water' || !product || wastewater);

  return (
    <main>
      <PageHeader title={t('register.title')} page="register" />
      <ErrorBox error={error} />
      <div className="form">
        <label>
          {t('col.product')}
          <select
            id="register-product"
            value={prodCode}
            onChange={(e) => {
              setConfirming(false);
              setProdCode(e.target.value);
              // the water step exists only for products grown on wastewater
              const next = products.find((p) => p.prod_code === e.target.value);
              if (next && next.cultivation_medium_code !== 'WW') set('raw_water', { mode: 'none', id: '' });
            }}
          >
            <option value="">{t('register.choose_product')}</option>
            {products.map((p) => (
              <option key={p.prod_code} value={p.prod_code}>
                {p.prod_code} · {p.product_name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted small">{t('register.order_help')}</p>
      <table>
        <thead>
          <tr>
            <th>{t('register.step')}</th>
            <th>{t('register.record')}</th>
            <th>{t('register.identifier')}</th>
            <th>{t('register.owner')}</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((k) => {
            const s = steps[k];
            const existing = nodes.filter((n) => n.kind === k);
            const up = SAME_AS[k];
            return (
              <tr key={k} data-kind={k}>
                <td>
                  <strong>{t(`kind.${k}`)}</strong>
                  <div className="small muted">{t(`register.help.${k}`)}</div>
                </td>
                <td>
                  <select value={s.mode} onChange={(e) => set(k, { mode: e.target.value as Mode, id: '' })}>
                    <option value="new">{t('register.mode.new')}</option>
                    <option value="existing" disabled={existing.length === 0}>
                      {t('register.mode.existing')}
                    </option>
                    {up && <option value="same">{t('register.mode.same', { step: t(`kind.${up}`) })}</option>}
                    <option value="none">{t('register.mode.none')}</option>
                  </select>
                </td>
                <td>
                  {s.mode === 'new' && (
                    <input
                      value={s.id}
                      placeholder={t(`register.example.${k}`)}
                      onChange={(e) => set(k, { id: e.target.value })}
                    />
                  )}
                  {s.mode === 'existing' && (
                    <select value={s.id} onChange={(e) => set(k, { id: e.target.value })}>
                      <option value="">{t('register.choose')}</option>
                      {existing.map((n) => (
                        <option key={n.node_id} value={n.node_id}>
                          {n.node_id}
                          {n.prod_code ? ` · ${n.prod_code}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {s.mode === 'same' && <span className="muted small">{t('register.same_note')}</span>}
                </td>
                <td>
                  {s.mode === 'new' && (
                    <select
                      value={s.owner}
                      disabled={ownOnly}
                      onChange={(e) => set(k, { owner: e.target.value })}
                    >
                      <option value="">{t('register.choose')}</option>
                      {orgs.map((o) => (
                        <option key={o.org_code} value={o.org_code}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {s.mode === 'existing' && s.id && (
                    <span className="small muted">
                      {orgName(nodes.find((n) => n.node_id === s.id)?.owner_org ?? '')}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {plan.problems.length > 0 ? (
        <ul className="error">
          {plan.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : !confirming ? (
        <button onClick={() => setConfirming(true)}>{t('register.check')}</button>
      ) : (
        <section className="card">
          <h2>{t('register.confirm_title')}</h2>
          <p className="hint">{t('register.permanent')}</p>
          {plan.create.length > 0 && (
            <>
              <h3>{t('register.will_create', { count: plan.create.length })}</h3>
              <ul>
                {plan.create.map((n) => (
                  <li key={n.node_id}>
                    {t(`kind.${n.kind}`)} <strong>{n.node_id}</strong>
                    {n.prod_code ? ` · ${n.prod_code}` : ''} · {orgName(n.owner_org)}
                  </li>
                ))}
              </ul>
            </>
          )}
          {plan.add.length > 0 && (
            <>
              <h3>{t('register.will_link', { count: plan.add.length })}</h3>
              <ul>
                {plan.add.map((l) => (
                  <li key={`${l.from_node}|${l.link}`}>
                    <strong>{l.from_node}</strong> ← {t(`kind.${l.same_as ?? l.link}`)}{' '}
                    <strong>{l.to_node}</strong>
                    {current.some((c) => c.from_node === l.from_node && c.link === l.link) && (
                      <span className="small muted"> ({t('register.replaces')})</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          <button disabled={busy} onClick={() => void save()}>
            {t('register.save')}
          </button>{' '}
          <button className="secondary" disabled={busy} onClick={() => setConfirming(false)}>
            {t('register.back')}
          </button>
        </section>
      )}
      <p className="small">
        <Link to="/batches">{t('register.to_batches')}</Link>
      </p>
    </main>
  );
}
